import { Form, Input, Modal, Typography, message, Spin } from "antd";
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { TNA_TODO_ENDPOINT } from "../../../apiServices/API_ENDPOINTS";
import { getData, putData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import { NO_PERMISSION_REQUIRED } from "../../../routes/permissions";
import { cglFormatDate, hasPermission } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const TNATemplate = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const roleInfo = localStorage.getItem("roleInfo");
    if (roleInfo) {
      setUserInfo(JSON.parse(roleInfo));
    }
  }, []);

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHeldModalOpen, setIsHeldModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [taskId, setTaskId] = useState(0);

  const [currentRole, setCurrentRole] = useState(
    localStorage.getItem("roleInfo")
      ? JSON.parse(localStorage.getItem("roleInfo"))
      : null,
  );

  const [addCommentForm] = Form.useForm();
  const [heldCommentForm] = Form.useForm();
  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllTNATemplates = useCallback(
    async (filterValues) => {
      const { todoTask, date_range, buyer_id, responsible_person_id } =
        filterValues || {};

      setLoading(true);

      const query = `${TNA_TODO_ENDPOINT}`;
      const bodyData = {
        per_page: 10000,
        ...(buyer_id && { buyer_id: buyer_id }),
        ...(responsible_person_id && {
          responsible_person_id: responsible_person_id,
        }),
        ...(date_range && {
          date_range: [
            moment(date_range[0]).format("YYYY-MM-DD"),
            moment(date_range[1]).format("YYYY-MM-DD"),
          ],
          action_statuses: todoTask,
        }),
      };

      const response = await getData(query, false, bodyData);
      if (response && response?.status === 200) {
        setLoading(false);

        setTableData(response?.data?.data);
      } else {
        setLoading(false);
        message.error("Error getting bin data");
      }
    },
    [currentPage],
  );

  useEffect(() => {
    getAllTNATemplates(filterData);
  }, [refresh, filterData, getAllTNATemplates]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const closeModel = () => {
    setIsModalOpen(false);
    setTaskId(0);
    addCommentForm.resetFields();
  };

  const closeHeldModal = () => {
    setIsHeldModalOpen(false);
    setTaskId(0);
    heldCommentForm.resetFields();
  };

  const onAction = (taskId, status) => {
    try {
      if (status === 2) {
        setTaskId(taskId);
        setIsHeldModalOpen(true);
        return;
      }

      const api = `${TNA_TODO_ENDPOINT}/${taskId}/action-status`;
      putData(api, {
        action_status: status,
        held_status_comment: null,
      });
      refetch();
      message.success("Action Performed Successfully!");
    } catch (error) {
      message.error("Something went wrong!");
    }
  };

  const onHeldWithComment = (values) => {
    try {
      const api = `${TNA_TODO_ENDPOINT}/${taskId}/action-status`;
      putData(api, {
        action_status: 2,
        held_status_comment: values.comment || null,
      });
      closeHeldModal();
      refetch();
      message.success("Task marked as Held!");
    } catch (error) {
      message.error("Something went wrong!");
    }
  };

  const onComment = (taskId, comment) => {
    try {
      const api = `${TNA_TODO_ENDPOINT}/${taskId}/comment`;
      putData(api, {
        comment: comment,
      });
      closeModel();
      refetch();
    } catch (error) {
      message.error("Something went wrong!");
    }
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllTNATemplates}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="tna_todo"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                hideAddButton={true}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Todo List</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div
              className="todo-table"
              style={{
                padding: "5px 20px ",
                maxHeight: "calc(100vh)",
                overflowY: "auto",
                backgroundColor: "white",
              }}
            >
              {loading && !tableData?.length ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    width: "100%",
                  }}
                >
                  <Spin size="large" tip="Loading data..." />
                </div>
              ) : (
                <table style={{ width: "100%", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ position: "sticky", top: 0, zIndex: 2 }}>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "8%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Due Date
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Buyer
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Buying Office{" "}
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Sales Contract No{" "}
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "5%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        SL
                      </th>
                      {(currentRole?.name === "Super-Admin" ||
                        currentRole?.name === "Admin" ||
                        currentRole?.name === "IT") && (
                        <th
                          className="t-header bc-java"
                          style={{
                            width: "10%",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          Responsible Person
                        </th>
                      )}
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "12%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Task
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Style(s)
                      </th>
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        PO(s)
                      </th>
                      {userInfo &&
                        (userInfo.name === "Commercial & Shipping" ||
                          userInfo.name ===
                            "Head of Commercial & Shipping") && (
                          <th
                            className="t-header bc-java"
                            style={{
                              width: "8%",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            Invoices
                          </th>
                        )}
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "7%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Status
                      </th>
                      {currentRole?.name !== "Super-Admin" &&
                        currentRole?.name !== "Admin" &&
                        currentRole?.name !== "IT" && (
                          <th
                            className="t-header bc-java"
                            colSpan={2}
                            style={{
                              width: "14%",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            Action
                          </th>
                        )}
                      <th
                        className="t-header bc-java"
                        style={{
                          width: "10%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Comment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(tableData) && tableData.length ? (
                      tableData.map((rowData, rowIndex) => (
                        <Fragment key={rowIndex}>
                          {rowData.data.map((nestedData, nestedIndex) => {
                            const totalTaskDateRows =
                              nestedData.tna_plan_task.reduce(
                                (acc, task) =>
                                  acc + (task.task_dates?.length || 1),
                                0,
                              );

                            let currentTaskDateRowIndex = 0;

                            return (
                              <Fragment key={`${rowIndex}-${nestedIndex}`}>
                                {nestedData.tna_plan_task.map(
                                  (task, taskIndex) => {
                                    const taskDateRows =
                                      task.task_dates?.length || 1;

                                    return (
                                      task.task_dates || [{ status: "N/A" }]
                                    ).map((taskDate, taskDateIndex) => {
                                      const isFirstTaskDateInNestedData =
                                        currentTaskDateRowIndex === 0;
                                      const isFirstTaskDateInTask =
                                        taskDateIndex === 0;

                                      currentTaskDateRowIndex++;

                                      return (
                                        <tr
                                          key={`${rowIndex}-${nestedIndex}-${taskIndex}-${taskDateIndex}`}
                                        >
                                          {isFirstTaskDateInNestedData && (
                                            <td
                                              className="t-body"
                                              rowSpan={totalTaskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {cglFormatDate(rowData.date)}
                                            </td>
                                          )}

                                          {isFirstTaskDateInNestedData && (
                                            <td
                                              className="t-body"
                                              rowSpan={totalTaskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {nestedData?.buyer?.name}
                                            </td>
                                          )}

                                          {isFirstTaskDateInNestedData && (
                                            <td
                                              className="t-body"
                                              rowSpan={totalTaskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {nestedData?.buying_office?.name}
                                            </td>
                                          )}

                                          {isFirstTaskDateInNestedData && (
                                            <td
                                              className="t-body"
                                              rowSpan={totalTaskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {nestedData?.reference_no}
                                            </td>
                                          )}

                                          {isFirstTaskDateInTask && (
                                            <td
                                              className="t-body"
                                              rowSpan={taskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {taskIndex + 1}
                                            </td>
                                          )}

                                          {(currentRole?.name ===
                                            "Super-Admin" ||
                                            currentRole?.name === "Admin" ||
                                            currentRole?.name === "IT") &&
                                            isFirstTaskDateInTask && (
                                              <td
                                                className="t-body"
                                                rowSpan={taskDateRows}
                                                style={{
                                                  wordBreak: "break-word",
                                                  whiteSpace: "normal",
                                                  verticalAlign: "top",
                                                  padding: "8px",
                                                }}
                                              >
                                                {task?.responsible_person_name}
                                              </td>
                                            )}

                                          {isFirstTaskDateInTask && (
                                            <td
                                              className="t-body"
                                              rowSpan={taskDateRows}
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              {task?.title}
                                            </td>
                                          )}

                                          <td
                                            className="t-body"
                                            style={{
                                              wordBreak: "break-word",
                                              whiteSpace: "pre-wrap",
                                              verticalAlign: "top",
                                              padding: "8px",
                                            }}
                                          >
                                            {taskDate?.styles?.length
                                              ? taskDate.styles
                                                  .map(
                                                    (style) => style?.style_no,
                                                  )
                                                  .join("\n")
                                              : "N/A"}
                                          </td>

                                          <td
                                            className="t-body"
                                            style={{
                                              wordBreak: "break-word",
                                              whiteSpace: "pre-wrap",
                                              verticalAlign: "top",
                                              padding: "8px",
                                            }}
                                          >
                                            {taskDate?.purchase_order_maps
                                              ?.length
                                              ? taskDate.purchase_order_maps
                                                  .map((po) => po?.original_po)
                                                  .join("\n")
                                              : "N/A"}
                                          </td>

                                          {userInfo &&
                                            (userInfo.name ===
                                              "Commercial & Shipping" ||
                                              userInfo.name ===
                                                "Head of Commercial & Shipping") && (
                                              <td
                                                className="t-body"
                                                style={{
                                                  wordBreak: "break-word",
                                                  whiteSpace: "normal",
                                                  verticalAlign: "top",
                                                  padding: "8px",
                                                }}
                                              >
                                                <a
                                                  href={`/tna/invoice-list/?scid=${nestedData?.id}`}
                                                >
                                                  Invoices
                                                </a>
                                              </td>
                                            )}

                                          <td
                                            className="t-body"
                                            style={{
                                              wordBreak: "break-word",
                                              whiteSpace: "normal",
                                              verticalAlign: "top",
                                              padding: "8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                backgroundColor:
                                                  taskDate?.status === "Regular"
                                                    ? "#93cddd"
                                                    : taskDate?.status ===
                                                      "Overdue"
                                                    ? "#f66d6d"
                                                    : "#ffc000",
                                                color:
                                                  taskDate?.status === "Regular"
                                                    ? "#045b73"
                                                    : taskDate?.status ===
                                                      "Overdue"
                                                    ? "#0f0f0f"
                                                    : "#9f7803",
                                                border: "2px solid white",
                                                padding: "4px",
                                                textAlign: "center",
                                                wordWrap: "break-word",
                                              }}
                                            >
                                              <span>{taskDate?.status}</span>
                                            </div>
                                          </td>

                                          {currentRole?.name !==
                                            "Super-Admin" &&
                                            currentRole?.name !== "Admin" &&
                                            currentRole?.name !== "IT" && (
                                              <>
                                                <td
                                                  className="t-body"
                                                  style={{
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    verticalAlign: "top",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor:
                                                        "#007635",
                                                      color: "white",
                                                      padding: "5px",
                                                      border: "2px solid white",
                                                      textAlign: "center",
                                                      cursor: "pointer",
                                                    }}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      onAction(
                                                        taskDate.id || task.id,
                                                        3,
                                                      );
                                                    }}
                                                  >
                                                    <span>Done</span>
                                                  </div>
                                                </td>
                                                <td
                                                  className="t-body"
                                                  style={{
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    verticalAlign: "top",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor:
                                                        "#c00000",
                                                      color: "white",
                                                      padding: "5px",
                                                      border: "2px solid white",
                                                      textAlign: "center",
                                                      cursor: "pointer",
                                                    }}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      onAction(
                                                        taskDate.id || task.id,
                                                        2,
                                                      );
                                                      heldCommentForm.setFieldsValue(
                                                        {
                                                          comment:
                                                            taskDate?.held_status_comment ||
                                                            "",
                                                        },
                                                      );
                                                    }}
                                                  >
                                                    <span>
                                                      {taskDate?.action_status_string ===
                                                      "To Do"
                                                        ? "Held"
                                                        : "Held"}
                                                    </span>
                                                  </div>
                                                </td>
                                              </>
                                            )}

                                          {currentRole?.name !==
                                            "Super-Admin" &&
                                            currentRole?.name !== "Admin" &&
                                            currentRole?.name !== "IT" && (
                                              <td
                                                className="t-body"
                                                style={{
                                                  wordBreak: "break-word",
                                                  whiteSpace: "normal",
                                                  verticalAlign: "top",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    color: "blue",
                                                    textAlign: "center",
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() => {
                                                    setTaskId(
                                                      taskDate.id || task?.id,
                                                    );
                                                    setIsModalOpen(true);
                                                    addCommentForm.setFieldsValue(
                                                      {
                                                        comment:
                                                          taskDate?.comment ||
                                                          "",
                                                      },
                                                    );
                                                  }}
                                                >
                                                  <span>Add</span>
                                                </div>
                                              </td>
                                            )}

                                          {(currentRole?.name ===
                                            "Super-Admin" ||
                                            currentRole?.name === "Admin" ||
                                            currentRole?.name === "IT") && (
                                            <td
                                              className="t-body"
                                              style={{
                                                wordBreak: "break-word",
                                                whiteSpace: "pre-wrap",
                                                verticalAlign: "top",
                                                padding: "8px",
                                              }}
                                            >
                                              <div>
                                                <span>
                                                  {taskDate?.comment ?? "N/A"}
                                                </span>
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      );
                                    });
                                  },
                                )}
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={11}
                          style={{
                            textAlign: "center",
                            padding: "20px",
                            color: "#999",
                          }}
                        >
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              <Modal
                open={isModalOpen}
                onOk={() => addCommentForm.submit()}
                onCancel={closeModel}
                onClose={closeModel}
                title="Add Comment"
              >
                <Form
                  form={addCommentForm}
                  onFinish={(values) => onComment(taskId, values?.comment)}
                  layout="vertical"
                >
                  <Form.Item
                    name={"comment"}
                    label="Comment:"
                    rules={[
                      {
                        required: true,
                        message: "Please input your comment!",
                      },
                    ]}
                  >
                    <Input.TextArea placeholder="Add Comment" rows={5} />
                  </Form.Item>
                </Form>
              </Modal>

              <Modal
                open={isHeldModalOpen}
                onOk={() => heldCommentForm.submit()}
                onCancel={closeHeldModal}
                onClose={closeHeldModal}
                title="Why is this task held?"
              >
                <Form
                  form={heldCommentForm}
                  onFinish={onHeldWithComment}
                  layout="vertical"
                >
                  <Form.Item
                    name={"comment"}
                    label="Reason for holding this task:"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please provide a reason for holding this task!",
                      },
                    ]}
                  >
                    <Input.TextArea placeholder="Enter reason" rows={5} />
                  </Form.Item>
                </Form>
              </Modal>
            </div>
          </>
        }
      />
    </>
  );
};

export default TNATemplate;
